import React, { useState, useEffect } from 'react';
import Modal from './Modal.tsx';
import { CreditCard, Loader2 } from 'lucide-react';
import axios from '../utils/axios.ts';
import { useQuery } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import AsyncMemberSelect, { MemberOption } from '../components/ui/AsyncMemberSelect.tsx';

interface DisbursementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOtpRequired: (disbursementId: number, otpExpiresAt: string) => void;
  loanData?: {
    recipient_name: string;
    recipient_phone: string;
    principal: string | number;
    loan_id: number;
    member_id: number;
  };
}

export default function DisbursementModal({ isOpen, onClose, onOtpRequired, loanData }: DisbursementModalProps) {
  const [form, setForm] = useState({
    type: 'manual',
    member_id: '',
    recipient_name: '',
    recipient_phone: '',
    amount: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMember, setSelectedMember] = useState<MemberOption | null>(null);

  // If loanData is provided, pre-fill form and disable member/amount fields
  useEffect(() => {
    if (loanData) {
      setForm(f => ({
        ...f,
        recipient_name: loanData.recipient_name,
        recipient_phone: loanData.recipient_phone,
        amount: loanData.principal?.toString() ?? '',
        loan_id: loanData.loan_id,
      }));
    }
  }, [loanData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const isLoan = !!loanData;
      const payload = {
        ...form,
        amount: isLoan ? Number(loanData.principal) : parseFloat(form.amount),
        member_id: isLoan ? loanData.member_id : (form.member_id ? Number(form.member_id) : undefined),
        loan_id: loanData?.loan_id,
        type: isLoan ? 'loan' : 'manual',
      };
      const res = await axios.post('/disbursements', payload);
      if (res.data.success) {
        onOtpRequired(res.data.data.disbursement_id, res.data.data.otp_expires_at);
        onClose();
      } else {
        setError(res.data.message || 'Failed to initiate disbursement');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to initiate disbursement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Initiate Disbursement"
      footer={
        <>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300">Cancel</button>
          <button type="submit" form="disbursement-form" disabled={loading} className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 flex items-center">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Initiate
          </button>
        </>
      }
    >
      <form id="disbursement-form" onSubmit={handleSubmit} className="space-y-4">
        {!loanData && (
          <div>
            <label className="block text-sm font-medium mb-1">Member</label>
            <AsyncMemberSelect
              id="member"
              value={selectedMember}
              onChange={(selectedOption: any) => {
                setSelectedMember(selectedOption);
                setForm(f => ({ ...f, member_id: selectedOption?.value?.toString() || '' }));
              }}
              className="text-base"
              classNamePrefix="select"
              placeholder="Select a member..."
              required
            />
          </div>
        )}
        {!loanData && (
          <div>
            <label className="block text-sm font-medium mb-1">Amount</label>
            <input name="amount" type="number" min="1" value={form.amount} onChange={handleChange} className="w-full border rounded px-3 py-2" required />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium mb-1">Notes (optional)</label>
          <textarea name="notes" value={form.notes} onChange={handleChange} className="w-full border rounded px-3 py-2" />
        </div>
        {error && <div className="text-red-600 text-sm">{error}</div>}
      </form>
    </Modal>
  );
} 